import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import {
    get as _get,
    has as _has,
} from 'lodash';

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CopyIcon from '@mui/icons-material/FileCopy';

import HighlightText from '../../../../components/highlightText';

const ColumnGroupName = ({ d, search }) => {
    return (
        <div><HighlightText text={d.row.name} search={search}></HighlightText></div>
    );
};

ColumnGroupName.propTypes = {
    d: PropTypes.object.isRequired,
    search: PropTypes.string,
};

const ColumnGroupEnabled = ({
    d,
    updater,
}) => {
    if (!d.row.enabled && !_get(updater, `groups.${d.row.name}.enabled`, false)) {
        return (<div>0</div>);
    }
    return <div>{d.row.enabled_rules || 0}</div>;
};

ColumnGroupEnabled.propTypes = {
    d: PropTypes.object.isRequired,
    updater: PropTypes.object.isRequired,
};

const ColumnGroupTotal = ({ d }) => { return <div>{d.row.total_rules || 0}</div> };

ColumnGroupTotal.propTypes = {
    d: PropTypes.object.isRequired,
};

const ColumnGroupEnable = ({
    d,
    updater,
    handleGroupEnable,
    readonly,
}) => {
    let enabled = d.row.enabled;

    if (_has(d.row, 'total_rules')) {
        if (_has(updater.groups, d.row.name)) {
            const exists = _get(updater, `groups.${d.row.name}.enabled`, undefined);
            enabled = exists !== undefined ? exists : enabled;
        }
    }

    return (
        <FormControlLabel
            control={
                <Checkbox
                    disabled={readonly}
                    checked={enabled !== undefined ? !!enabled : !!d.row.enabled}
                    onClick={(e) => { handleGroupEnable(e, d) }}
                />
            }
        />
    );
};

ColumnGroupEnable.propTypes = {
    d: PropTypes.object.isRequired,
    updater: PropTypes.object.isRequired,
    handleGroupEnable: PropTypes.func.isRequired,
    readonly: PropTypes.bool,
};

const ColumnRuleName = ({
    d,
    theme,
    search,
}) => {
    return (
        <Box style={{ lineHeight: '18px' }}>
            <Box><HighlightText text={d.row.name} search={search}></HighlightText></Box>
            <Box display="flex">
                <Box>Rule ID: <HighlightText text={d.row.id} search={search}></HighlightText></Box>
                <Box pl={2}>Group: <HighlightText text={d.row.group} search={search}></HighlightText></Box>
                <Box pl={2}>Last Seen: {Moment(d.row.last_seen).format(theme.date.long)}</Box>
            </Box>
        </Box>
    );
};

ColumnRuleName.propTypes = {
    d: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    search: PropTypes.string,
};

const ColumnRulePurge = ({
    d,
    updater,
    handleRulePurge,
    readonly,
}) => {
    let purge = !d.row.keep;

    if (_has(updater.rules, d.row.id)) {
        if (_has(updater.rules[d.row.id], 'keep')) {
            purge = !updater.rules[d.row.id].keep;
        }
    }

    return (
        <FormControlLabel
            control={
                <Checkbox
                    disabled={readonly}
                    checked={purge}
                    onClick={(e) => { handleRulePurge(e, d) }}
                />
            }
        />
    );
};

ColumnRulePurge.propTypes = {
    d: PropTypes.object.isRequired,
    updater: PropTypes.object.isRequired,
    handleRulePurge: PropTypes.func.isRequired,
    readonly: PropTypes.bool,
};

const ColumnRuleEnable = ({
    d,
    updater,
    handleRuleEnable,
    readonly,
}) => {
    let enabled = d.row.enabled;

    if (_has(updater.rules, d.row.id)) {
        if (_has(updater.rules[d.row.id], 'enabled')) {
            enabled = updater.rules[d.row.id].enabled;
        }
    }

    return (
        <FormControlLabel
            control={
                <Checkbox
                    disabled={readonly}
                    checked={enabled}
                    onClick={(e) => { handleRuleEnable(e, d) }}
                />
            }
        />
    );
};

ColumnRuleEnable.propTypes = {
    d: PropTypes.object.isRequired,
    updater: PropTypes.object.isRequired,
    handleRuleEnable: PropTypes.func.isRequired,
    readonly: PropTypes.bool,
};

const ColumnRuleActions = ({
    d,
    handleDeleteRuleDialog,
    handleCopyRule,
    handleEditRule,
    readonly,
    managed,
}) => {
    return (
        <Box>
            {!readonly && <Box p={1} display="flex" justifyContent="flex-end">
                {
                    managed ? <Box>
                        <Tooltip title="Rules must be managed by the MC2.">
                            <span>
                                <IconButton disabled size="small" aria-label="delete rule"><DeleteIcon /></IconButton>
                            </span>
                        </Tooltip>
                        {d.row.group !== 'custom_rules'
                            ? <Tooltip title="Rules must be managed by the MC2.">
                                <span>
                                    <IconButton disabled size="small" aria-label="copy rule"><CopyIcon /></IconButton>
                                </span>
                            </Tooltip>
                            : <Tooltip title="Rules must be managed by the MC2.">
                                <span>
                                    <IconButton disabled size="small" aria-label="edit rule"><EditIcon /></IconButton>
                                </span>
                            </Tooltip>
                        }
                    </Box>
                        : <Box>
                            <IconButton size="small" aria-label="delete rule" onClick={e => { handleDeleteRuleDialog(e, d) }}><DeleteIcon /></IconButton>
                            {d.row.group !== 'custom_rules'
                                ? <IconButton size="small" aria-label="copy rule" onClick={e => { handleCopyRule(e, d) }}><CopyIcon /></IconButton>
                                : <IconButton size="small" aria-label="edit rule" onClick={e => { handleEditRule(e, d) }}><EditIcon /></IconButton>
                            }
                        </Box>
                }
            </Box>}
        </Box>
    );
};

ColumnRuleActions.propTypes = {
    d: PropTypes.object.isRequired,
    updater: PropTypes.object.isRequired,
    handleDeleteRuleDialog: PropTypes.func.isRequired,
    handleCopyRule: PropTypes.func.isRequired,
    handleEditRule: PropTypes.func.isRequired,
    readonly: PropTypes.bool,
    managed: PropTypes.bool,
};

export {
    ColumnGroupName,
    ColumnGroupEnabled,
    ColumnGroupTotal,
    ColumnGroupEnable,
    ColumnRuleName,
    ColumnRulePurge,
    ColumnRuleEnable,
    ColumnRuleActions,
};
